/**
 * Translate popover — proxies real radio inputs to GTranslate's hidden
 * anchors, tracks translation completion via popover-title text mutation,
 * and shows a throbber while it runs.
 */

import { initPopover } from "./popover.js";

const toggle = document.querySelector(".translate-toggle");
const popover = document.getElementById("translate-popover");

if (toggle && popover) {
  // Warm up Google's translate script the moment the popover opens so the
  // click below actually does something — even for keyboard-only users who
  // never trigger GTranslate's pointerenter-based lazy loader.
  const ctrl = initPopover({ popover, toggle, onOpen: warmUp });

  popover.addEventListener("change", (e) => {
    if (!e.target.matches(".translate-radio")) return;
    const targetLang = e.target.value;
    const target = popover.querySelector(
      `.gtranslate-host .glink[data-gt-lang="${targetLang}"]`,
    );
    if (!target) return;

    // Start tracking BEFORE the click so we don't miss a fast cached
    // translation on the second+ call.
    trackTranslation(targetLang, popover, ctrl);
    target.click();
  });

  // Sync the radio state on load. If the page loaded with a translation
  // cookie already applied, the anchor with `.gt-current-lang` reflects
  // reality — the HTML-default-checked radio doesn't.
  syncRadiosToCurrentLang(popover);
}

function syncRadiosToCurrentLang(popover) {
  const check = () => {
    const active = popover.querySelector(
      ".gtranslate-host .glink.gt-current-lang",
    );
    if (!active) return false;
    const radio = popover.querySelector(
      `.translate-radio[value="${active.dataset.gtLang}"]`,
    );
    if (radio) radio.checked = true;
    return true;
  };
  if (check()) return;
  // GTranslate may not have added `.gt-current-lang` yet — wait for it.
  const obs = new MutationObserver(() => {
    if (check()) obs.disconnect();
  });
  obs.observe(popover, { subtree: true, attributes: true, attributeFilter: ["class"] });
}

// GTranslate lazy-loads Google's translate_a/element.js on `pointerenter`
// of the switcher to save ~500KB for users who never translate. We mirror
// its internal `load_tlib` (base.js:104) so the script loads as soon as
// the popover opens — regardless of hover, so keyboard users aren't locked
// out.
function warmUp() {
  if (window.gt_translate_script) return;
  const script = document.createElement("script");
  script.src =
    "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit2";
  window.gt_translate_script = script;
  document.body.appendChild(script);
}

function trackTranslation(targetLang, popover, ctrl) {
  // Skip if the already-active language was re-selected — nothing mutates.
  const active = popover.querySelector(
    ".gtranslate-host .glink.gt-current-lang",
  );
  if (active?.dataset.gtLang === targetLang) {
    ctrl.close();
    return;
  }

  const title = popover.querySelector(".popover-title");
  if (!title) {
    ctrl.close();
    return;
  }

  // Snapshot the title text before GTranslate's click handler runs; we'll
  // consider the translation done when it changes.
  const initialText = title.textContent;

  popover.classList.add("is-translating");

  const finish = () => {
    clearTimeout(failsafe);
    observer.disconnect();
    popover.classList.remove("is-translating");
    ctrl.close();
  };

  const observer = new MutationObserver(() => {
    if (title.textContent !== initialText) finish();
  });
  observer.observe(title, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Failsafe: bail after 8s so the throbber can't get stuck.
  const failsafe = setTimeout(finish, 8000);
}
